import { NavigateFn, useSearch } from '@tanstack/react-router'
import { Column, ColumnDef, ColumnFiltersState } from '@tanstack/react-table'
import CantileverButton from 'components/CantileverButton'
import SearchBar from 'components/SearchBar'
import React from 'react'
import styled, { css } from 'styled-components'

import { ColumnConfiguration } from './ColumnConfiguration'
import { FilterBar } from './filters/FilterBar'

const StyledFilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 2.5rem;
`

const FilterGroupStyles = css`
  display: flex;
  gap: 4px;
  height: 100%;
  &:empty {
    display: none;
  }
`
const FilterButtonGroup = styled.div`
  ${FilterGroupStyles}
  min-width: 0;
  width: 100%;
`

const SearchContainer = styled.div`
  ${FilterGroupStyles}
  margin-left: auto;
`

export const TableTopBar = <TData,>({
  columns,
  setGlobalFilter,
  getColumn,
  showFilterBar,
  showGlobalFilter,
  showColumnConfig,
  leadingContent,
  initialColumnFilters,
  navigate,
  clearAll,
  disableUrlFilters,
  defaultVisibleColumnIds,
  bulkEditEnabled,
  onClickBulkEdit,
  resultCount,
}: {
  columns: ColumnDef<TData>[]
  setGlobalFilter: React.Dispatch<unknown>
  getColumn: (columnID: string) => Column<TData, unknown> | undefined
  showFilterBar: boolean
  showGlobalFilter: boolean
  showColumnConfig: boolean
  leadingContent?: React.ReactNode
  initialColumnFilters?: ColumnFiltersState
  navigate: NavigateFn
  clearAll?: () => void
  disableUrlFilters?: boolean
  defaultVisibleColumnIds?: string[]
  bulkEditEnabled?: boolean
  onClickBulkEdit?: () => void
  resultCount?: number
}) => {
  const search = useSearch({ strict: false })
  const searchParam = search.search as string
  const [searchValue, setSearchValue] = React.useState(searchParam || '')
  const clearAllRef = React.useRef(clearAll)

  // Sync with URL on mount and URL changes
  React.useEffect(() => {
    setSearchValue(searchParam || '')
    setGlobalFilter(searchParam || '')
  }, [searchParam, setGlobalFilter])

  const handleFilterInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget
    setSearchValue(value)
    setGlobalFilter(value)
    if (!disableUrlFilters) {
      navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          search: value || undefined,
        }),
      })
    }
  }

  React.useEffect(() => {
    if (clearAll) {
      clearAllRef.current = () => {
        clearAll()
        setSearchValue('')
        setGlobalFilter('')
        if (!disableUrlFilters) {
          navigate({
            to: '.',
            search: (prev) => ({
              ...prev,
              search: undefined,
            }),
          })
        }
      }
    }
  }, [clearAll, navigate, setGlobalFilter, disableUrlFilters])

  return (
    <StyledFilterContainer>
      {leadingContent ? (
        <FilterButtonGroup>{leadingContent}</FilterButtonGroup>
      ) : null}
      {columns.length > 0 && showFilterBar && (
        <FilterButtonGroup>
          <FilterBar
            columns={columns}
            getColumn={getColumn}
            initialColumnFilters={initialColumnFilters}
            navigate={navigate}
            disableUrlFilters={disableUrlFilters}
          />
          {bulkEditEnabled && (
            <CantileverButton
              primary
              style={{ width: '7rem' }}
              onClick={onClickBulkEdit}>
              Bulk Actions
            </CantileverButton>
          )}
        </FilterButtonGroup>
      )}
      {showGlobalFilter && (
        <SearchContainer>
          <SearchBar
            placeholder='Search'
            value={searchValue}
            onChange={handleFilterInputChange}
            resultCount={resultCount}
          />
          {showColumnConfig && (
            <ColumnConfiguration
              columns={columns}
              getColumn={getColumn}
              defaultVisibleColumnIds={defaultVisibleColumnIds}
            />
          )}
        </SearchContainer>
      )}
    </StyledFilterContainer>
  )
}
